((root) => {
  const positive = v => /^\d+(?:[.,]\d+)?$/.test(String(v ?? '').trim()) && Number(String(v).replace(',', '.')) > 0 ? Number(String(v).replace(',', '.')) : null;
  const clean = v => String(v ?? '').replace(/<[^>]*>/g, ' ').replace(/&nbsp;|&#160;/gi, ' ').replace(/&amp;/gi, '&').replace(/&#(?:x([0-9a-f]+)|(\d+));/gi, (_, hex, dec) => String.fromCodePoint(Math.min(0x10ffff, parseInt(hex || dec, hex ? 16 : 10)))).replace(/\s+/g, ' ').trim();
  const normal = v => clean(v).toLocaleLowerCase('sv');
  const labels = {
    fiber: /^(fiberinnehåll|material|innehåll|fiber)$/i,
    weight: /^(nystanvikt|vikt per nystan|vikt)$/i,
    meters: /^(meter per nystan|löplängd|längd per nystan)$/i,
    gauge: /^(stickfasthet|masktäthet)(?:.*10.*cm)?$/i,
    needle: /^(rek\.? sticka|rekommenderade stickor|stickstorlek|stickor)$/i,
    hook: /^(rek\.? virknål|virknål|virknålsstorlek)$/i,
    care: /^(tvättråd|skötselråd)$/i
  };
  function rows(html) {
    return [...String(html || '').matchAll(/<tr\b[^>]*>([\s\S]*?)<\/tr>/gi)].map(m => [...m[1].matchAll(/<(?:td|th)\b[^>]*>([\s\S]*?)<\/(?:td|th)>/gi)].map(c => clean(c[1]))).filter(r => r.length === 2);
  }
  function unit(text, suffix) {
    const m = clean(text).match(new RegExp('^(?:ca\\.?\\s*)?(\\d+(?:[.,]\\d+)?)\\s*' + suffix + '(?:\\s*(?:nystan|härva|\\((?:nystan|härva)\\)))?$','i'));
    return m ? positive(m[1]) : null;
  }
  function length(text, weight) {
    const single = unit(text, 'm');
    if (single) return single;
    const m = clean(text).match(/^(?:ca\.?\s*)?(\d+(?:[.,]\d+)?)\s*m\s*(?:per|\/)\s*(\d+(?:[.,]\d+)?)\s*g(?:\s*(?:nystan|härva))?$/i);
    return m && positive(m[2]) === weight ? positive(m[1]) : null;
  }
  function gauge(text) {
    const s = clean(text).replace(/×/g, 'x');
    if (!/10\s*(?:x\s*10\s*)?cm/i.test(s) || /\d\s*[-–]\s*\d/.test(s)) return null;
    const m = s.match(/^(?:ca\.?\s*)?(\d+(?:[.,]\d+)?)\s*(?:m(?:askor)?\b|x\s*\d+\s*(?:v(?:arv)?\b|på))/i);
    return m ? positive(m[1]) : null;
  }
  const fiberAliases = {polyamid:'nylon',merino:'ull',merinoull:'ull',wool:'ull',cotton:'bomull',silk:'silke',siden:'silke',cashmere:'kashmir',alpaca:'alpacka',linen:'lin',acrylic:'akryl'};
  const fiberKey = values => [...new Set((Array.isArray(values) ? values : []).map(v => fiberAliases[normal(v)] || normal(v)).filter(Boolean))].sort().join('|');
  function specification(product) {
    const table = rows(product.description), conflicts = [];
    const cell = key => {
      const matches = [...new Set(table.filter(([label]) => labels[key].test(label)).map(([, value]) => value))];
      if (matches.length > 1) conflicts.push(key);
      return matches.length === 1 ? matches[0] : '';
    };
    const text = Object.fromEntries(Object.keys(labels).map(key => [key, cell(key)]));
    const weight = positive(product.weight) || unit(text.weight, 'g');
    const meters = positive(product.meters) || length(text.meters, weight);
    const gaugeText = clean(product.gaugeText || text.gauge);
    for (const [key, stored, parsed] of [['weight', positive(product.weight), unit(text.weight, 'g')], ['meters', positive(product.meters), length(text.meters, weight)], ['gauge', positive(product.gauge), gauge(text.gauge)]]) {
      if (stored && parsed && stored !== parsed) conflicts.push(key);
    }
    if (/\d\s*[-–]\s*\d/.test(gaugeText) || /\d\s*[-–]\s*\d/.test(text.gauge)) conflicts.push('gauge');
    const uses = [...new Set((Array.isArray(product.uses) ? product.uses : []).map(clean).filter(Boolean))];
    return {...product, weight, meters, gauge: positive(product.gauge) || gauge(gaugeText), gaugeText, materials: Array.isArray(product.materials) ? product.materials.filter(v=>typeof v==='string') : [], fiber: clean(product.fiber || text.fiber), needle: clean(product.needle || text.needle), hook: clean(product.hook || text.hook), care: clean(product.care || text.care), uses, conflicts, specText: clean(product.description), sourceRows: table};
  }
  function similar(source, target) {
    if (!source || !target || source.handle === target.handle || !target.available || source.conflicts?.length || target.conflicts?.length) return null;
    if (![source.weight, source.meters, source.gauge, target.weight, target.meters, target.gauge].every(positive)) return null;
    if (!fiberKey(source.materials) || fiberKey(source.materials) !== fiberKey(target.materials)) return null;
    const special = /mohair|pelz|pälsgarn|effektgarn|boucl|chenille|följetråd|blow.?yarn|reflex|glitter|paljett|lurex|\brafia\b|\bbling\b/;
    if ([source, target].some(p=>special.test(normal([p.title, p.specText, p.materials?.join(' ')].join(' '))))) return null;
    const gaugeDifference = Math.abs(target.gauge / source.gauge - 1);
    const lengthDifference = Math.abs((target.meters / target.weight) / (source.meters / source.weight) - 1);
    if (gaugeDifference > .05 + 1e-9 || lengthDifference > .15 + 1e-9) return null;
    return {score: 100 - gaugeDifference * 300 - lengthDifference * 100, gaugeDifference, lengthDifference};
  }
  function selection(value) {
    return Array.isArray(value) ? [...new Set(value.filter(v=>typeof v==='string' && /^[-a-z0-9]{1,200}$/.test(v)))].slice(0,4) : [];
  }
  function recommendations(source, products, limit = 4) {
    const eligible = [...new Map(products.filter(p=>p.available && p.handle !== source.handle && selection([p.handle]).length).map(p=>[p.handle,p])).values()];
    const fibres = new Set(fiberKey(source.materials).split('|').filter(Boolean));
    const uses = new Set((source.uses || []).map(normal));
    const tie = handle => [...source.handle + ':' + handle].reduce((hash,c)=>Math.imul(hash ^ c.charCodeAt(0),16777619) >>> 0,2166136261);
    return eligible.map(p=>({p,match:similar(source,p),affinity:
      fiberKey(p.materials).split('|').filter(v=>v && fibres.has(v)).length * 4 +
      (p.uses || []).filter(v=>uses.has(normal(v))).length * 2 +
      (source.vendor && normal(source.vendor) === normal(p.vendor) ? 1 : 0)
    })).sort((a,b)=>Number(!!b.match)-Number(!!a.match) || (a.match && b.match ? b.match.score-a.match.score : b.affinity-a.affinity) || tie(a.p.handle)-tie(b.p.handle) || a.p.handle.localeCompare(b.p.handle)).slice(0,Math.max(0,Math.min(4,limit))).map(item=>item.p);
  }
  const api = {positive, clean, rows, labels, unit, length, gauge, specification, similar, selection, recommendations};
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else root.KickisYarnSpecs = api;
})(typeof window === 'undefined' ? globalThis : window);
